import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, TextField, Paper, Grid, Card, CardContent, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Alert,
  LinearProgress, MenuItem, Tooltip, IconButton, Autocomplete
} from '@mui/material';
import {
  Language as WebsiteIcon, Refresh as RefreshIcon, Check as CheckIcon,
  Close as RejectIcon, Replay as RetryIcon, OpenInNew as OpenIcon
} from '@mui/icons-material';

const API_ROOT = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api';
const API_BASE = `${API_ROOT}/website-finder`;

// Tags carry up to 4 company tags, per the tagging manual (most companies get exactly 1).
const MAX_TAGS = 4;

interface Stats {
  missingWebsite: number;
  remainingToSearch: number;
  pending: number;
  approved: number;
  rejected: number;
  notFound: number;
}

interface IndustryTag {
  id: number;
  name: string;
  newsletter: string | null;
}

interface ResultRow {
  id: number;
  company_id: number;
  name: string;
  company_address: string | null;
  location: string | null;
  provider: string | null;
  candidate_url: string | null;
  candidate_domain: string | null;
  confidence: string | null;
  reason: string | null;
  url_status: string;
  status: string;
  industry_tag: number | null;
  industry_tag_two: number | null;
  industry_tag_three: number | null;
  industry_tag_four: number | null;
}

const PROVIDER_LABEL: Record<string, string> = { gemini: 'Gemini', you: 'You.com', claude: 'Claude' };

const statusColor = (s: string): 'success' | 'warning' | 'error' | 'default' => {
  if (s === 'healthy') return 'success';
  if (s === 'protected') return 'warning';
  if (s === 'broken') return 'error';
  return 'default';
};

const WebsiteFinderPage: React.FC = () => {
  const [stats, setStats] = useState<Stats | null>(null);
  const [results, setResults] = useState<ResultRow[]>([]);
  const [statusFilter, setStatusFilter] = useState<'pending' | 'approved' | 'rejected' | 'not_found'>('pending');
  const [batchSize, setBatchSize] = useState(15);
  const [provider, setProvider] = useState<'gemini' | 'you' | 'claude'>('gemini');
  const [running, setRunning] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [runSummary, setRunSummary] = useState<any>(null);
  const [edits, setEdits] = useState<Record<number, string>>({});
  const [busyIds, setBusyIds] = useState<Set<number>>(new Set());
  const [industryTags, setIndustryTags] = useState<IndustryTag[]>([]);
  const [tagSelections, setTagSelections] = useState<Record<number, number[]>>({});

  const token = () => localStorage.getItem('ems_access_token') || localStorage.getItem('token');
  const authFetch = (path: string, options: RequestInit = {}) => fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token() ? { Authorization: `Bearer ${token()}` } : {}),
      ...options.headers
    }
  });

  const loadStats = useCallback(async () => {
    try {
      const res = await authFetch('/stats');
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || data.error);
      setStats(data);
    } catch (err: any) {
      setError(err.message);
    }
  }, []);

  const loadResults = useCallback(async (status: string) => {
    try {
      setLoading(true);
      const res = await authFetch(`/results?status=${status}`);
      const data: ResultRow[] = await res.json();
      if (!res.ok) throw new Error((data as any).message || (data as any).error);
      setResults(data);
      // Prefill the tag picker from whatever tags the company already has
      setTagSelections((prev) => {
        const next = { ...prev };
        data.forEach((r) => {
          if (next[r.id]) return;
          const existing = [r.industry_tag, r.industry_tag_two, r.industry_tag_three, r.industry_tag_four]
            .filter((t): t is number => t !== null && t !== undefined);
          if (existing.length) next[r.id] = existing;
        });
        return next;
      });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadIndustryTags = useCallback(async () => {
    try {
      const res = await fetch(`${API_ROOT}/industry-tags?status=1`, {
        headers: token() ? { Authorization: `Bearer ${token()}` } : {}
      });
      const data = await res.json();
      if (res.ok) setIndustryTags((data.data || []).map((t: any) => ({ id: t.id, name: t.name, newsletter: t.newsletter })));
    } catch (_) { /* tag picker just stays empty if this fails */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The "Search with" dropdown defaults to whatever's saved in Settings > Search & LLM,
  // not hardcoded to Gemini - otherwise picking You.com/Claude here doesn't stick across reloads.
  const loadDefaultProvider = useCallback(async () => {
    try {
      const res = await fetch(`${API_ROOT}/search-settings/llm`, {
        headers: token() ? { Authorization: `Bearer ${token()}` } : {}
      });
      const data = await res.json();
      if (res.ok && ['gemini', 'you', 'claude'].includes(data.search_provider)) {
        setProvider(data.search_provider);
      }
    } catch (_) { /* keep the gemini default if this fails */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadStats();
    loadIndustryTags();
    loadDefaultProvider();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Picking a provider here also becomes the new Settings > Search & LLM default, so the
  // choice actually sticks across reloads instead of silently reverting to Gemini.
  const handleProviderChange = async (value: 'gemini' | 'you' | 'claude') => {
    setProvider(value);
    try {
      await fetch(`${API_ROOT}/search-settings/llm`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token() ? { Authorization: `Bearer ${token()}` } : {})
        },
        body: JSON.stringify({ search_provider: value })
      });
    } catch (_) { /* the dropdown still works for this session even if saving the default fails */ }
  };

  useEffect(() => {
    loadResults(statusFilter);
  }, [statusFilter, loadResults]);

  const handleRun = async () => {
    try {
      setRunning(true);
      setError(null);
      setRunSummary(null);
      const res = await authFetch('/run', { method: 'POST', body: JSON.stringify({ limit: batchSize, provider }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || data.error);
      setRunSummary(data);
      await loadStats();
      if (statusFilter === 'pending') await loadResults('pending');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setRunning(false);
    }
  };

  const withBusy = async (ids: number[], fn: () => Promise<void>) => {
    setBusyIds((prev) => new Set([...Array.from(prev), ...ids]));
    try {
      await fn();
    } finally {
      setBusyIds((prev) => {
        const next = new Set(prev);
        ids.forEach((id) => next.delete(id));
        return next;
      });
    }
  };

  const approve = (ids: number[]) => withBusy(ids, async () => {
    const editPayload: Record<number, string> = {};
    const tagPayload: Record<number, number[]> = {};
    ids.forEach((id) => {
      if (edits[id]) editPayload[id] = edits[id];
      if (tagSelections[id] && tagSelections[id].length) tagPayload[id] = tagSelections[id];
    });
    const res = await authFetch('/approve', { method: 'POST', body: JSON.stringify({ ids, edits: editPayload, tags: tagPayload }) });
    const data = await res.json();
    if (!res.ok) { setError(data.message || data.error); return; }
    await Promise.all([loadStats(), loadResults(statusFilter)]);
  });

  const tagByName = (name: string) => industryTags.find((t) => t.name.toLowerCase() === name.toLowerCase());
  const quickTag = (resultId: number, name: string) => {
    const tag = tagByName(name);
    if (tag) setTagSelections((prev) => ({ ...prev, [resultId]: [tag.id] }));
  };

  const reject = (ids: number[]) => withBusy(ids, async () => {
    const res = await authFetch('/reject', { method: 'POST', body: JSON.stringify({ ids }) });
    const data = await res.json();
    if (!res.ok) { setError(data.message || data.error); return; }
    await Promise.all([loadStats(), loadResults(statusFilter)]);
  });

  const retry = (ids: number[]) => withBusy(ids, async () => {
    const res = await authFetch('/retry', { method: 'POST', body: JSON.stringify({ ids }) });
    const data = await res.json();
    if (!res.ok) { setError(data.message || data.error); return; }
    await Promise.all([loadStats(), loadResults(statusFilter)]);
  });

  const approveAllHealthy = () => {
    const ids = results.filter((r) => r.status === 'pending' && r.url_status === 'healthy').map((r) => r.id);
    if (ids.length) approve(ids);
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1100, mx: 'auto' }}>
      <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <WebsiteIcon sx={{ color: '#3b82f6' }} /> Website Finder
      </Typography>
      <Typography variant="body2" sx={{ color: '#475569', mb: 3 }}>
        Uses Gemini + Google Search (same key as Settings → Search LLM) to find the official website for companies that don't have one.
        Every match is staged here for review before it's written to the company record — nothing is applied automatically.
        Pick up to 4 tags per company (per the tagging manual) before approving, so a found website doesn't sit untagged.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Missing website', value: stats?.missingWebsite, color: '#0f172a' },
          { label: 'Not yet searched', value: stats?.remainingToSearch, color: '#3b82f6' },
          { label: 'Pending review', value: stats?.pending, color: '#f59e0b' },
          { label: 'Approved', value: stats?.approved, color: '#10b981' },
          { label: 'Rejected', value: stats?.rejected, color: '#ef4444' },
          { label: 'Not found', value: stats?.notFound, color: '#64748b' }
        ].map((s) => (
          <Grid item xs={6} sm={4} md={2} key={s.label}>
            <Card variant="outlined" sx={{ borderRadius: '10px' }}>
              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>{s.label}</Typography>
                <Typography variant="h6" sx={{ fontWeight: 800, color: s.color }}>{s.value ?? '-'}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: '12px', mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          select
          label="Search with"
          size="small"
          value={provider}
          onChange={(e) => handleProviderChange(e.target.value as 'gemini' | 'you' | 'claude')}
          sx={{ width: 150 }}
        >
          <MenuItem value="gemini">Gemini</MenuItem>
          <MenuItem value="you">You.com</MenuItem>
          <MenuItem value="claude">Claude</MenuItem>
        </TextField>
        <TextField
          label="Batch size"
          type="number"
          size="small"
          value={batchSize}
          onChange={(e) => setBatchSize(Math.min(Math.max(parseInt(e.target.value, 10) || 1, 1), 50))}
          sx={{ width: 120 }}
          helperText="1-50 per run"
        />
        <Button variant="contained" onClick={handleRun} disabled={running} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', px: 3 }}>
          {running ? 'Searching...' : 'Find Websites'}
        </Button>
        <IconButton size="small" onClick={() => { loadStats(); loadResults(statusFilter); }}>
          <RefreshIcon fontSize="small" />
        </IconButton>
        {running && <Typography variant="caption" sx={{ color: '#64748b' }}>This can take a minute for larger batches — one search per company.</Typography>}
        {runSummary && !running && (
          <Typography variant="body2" sx={{ color: '#0f172a' }}>
            Last run ({PROVIDER_LABEL[runSummary.provider] || runSummary.provider}): {runSummary.found} found, {runSummary.notFound} not found, {runSummary.errors} errors (of {runSummary.processed}).
          </Typography>
        )}
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
        <TextField
          select
          size="small"
          label="Showing"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as any)}
          sx={{ width: 200 }}
        >
          <MenuItem value="pending">Pending review</MenuItem>
          <MenuItem value="approved">Approved</MenuItem>
          <MenuItem value="rejected">Rejected</MenuItem>
          <MenuItem value="not_found">Not found</MenuItem>
        </TextField>
        {statusFilter === 'pending' && (
          <Tooltip title="Applies only the website, not tags — bulk-approved companies still need tagging individually">
            <Button size="small" variant="outlined" color="success" onClick={approveAllHealthy} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}>
              Approve all healthy
            </Button>
          </Tooltip>
        )}
      </Box>

      {loading && <LinearProgress sx={{ mb: 1 }} />}

      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '12px' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Company</TableCell>
              <TableCell>Address</TableCell>
              <TableCell>Candidate website</TableCell>
              <TableCell>Confidence</TableCell>
              <TableCell>Link status</TableCell>
              {statusFilter !== 'not_found' && <TableCell sx={{ minWidth: 220 }}>Tags</TableCell>}
              {statusFilter !== 'not_found' && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {results.length === 0 && !loading && (
              <TableRow><TableCell colSpan={7} align="center" sx={{ color: '#94a3b8', py: 3 }}>Nothing here.</TableCell></TableRow>
            )}
            {results.map((r) => (
              <TableRow key={r.id} hover>
                <TableCell sx={{ fontWeight: 600, maxWidth: 180 }}>{r.name}</TableCell>
                <TableCell sx={{ color: '#64748b', maxWidth: 220, fontSize: '0.75rem' }}>{r.company_address || r.location || '-'}</TableCell>
                <TableCell sx={{ maxWidth: 260 }}>
                  {r.provider && (
                    <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block' }}>
                      via {PROVIDER_LABEL[r.provider] || r.provider}
                    </Typography>
                  )}
                  {r.status === 'pending' ? (
                    <TextField
                      size="small"
                      fullWidth
                      value={edits[r.id] ?? r.candidate_url ?? ''}
                      onChange={(e) => setEdits((prev) => ({ ...prev, [r.id]: e.target.value }))}
                      sx={{ '& .MuiInputBase-input': { fontSize: '0.8rem' } }}
                    />
                  ) : r.candidate_url ? (
                    <a href={r.candidate_url} target="_blank" rel="noreferrer" style={{ fontSize: '0.8rem' }}>
                      {r.candidate_domain || r.candidate_url} <OpenIcon sx={{ fontSize: 12, verticalAlign: 'middle' }} />
                    </a>
                  ) : (
                    <Tooltip title={r.reason || ''}><span style={{ color: '#94a3b8' }}>no match</span></Tooltip>
                  )}
                </TableCell>
                <TableCell>
                  {r.confidence && <Chip size="small" label={r.confidence} color={r.confidence === 'high' ? 'success' : r.confidence === 'medium' ? 'warning' : 'default'} />}
                </TableCell>
                <TableCell>
                  {r.candidate_url && <Chip size="small" label={r.url_status} color={statusColor(r.url_status)} />}
                </TableCell>
                {statusFilter !== 'not_found' && (
                  <TableCell>
                    <Autocomplete
                      multiple
                      size="small"
                      disabled={r.status !== 'pending'}
                      options={industryTags}
                      getOptionLabel={(t) => t.name}
                      value={industryTags.filter((t) => (tagSelections[r.id] || []).includes(t.id))}
                      onChange={(_, value) => {
                        if (value.length > MAX_TAGS) return;
                        setTagSelections((prev) => ({ ...prev, [r.id]: value.map((t) => t.id) }));
                      }}
                      renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                          {option.name}{option.newsletter ? ` (${option.newsletter})` : ''}
                        </li>
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            size="small"
                            label={option.name}
                            {...getTagProps({ index })}
                            key={option.id}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField {...params} placeholder={r.status === 'pending' ? 'Up to 4 tags' : undefined} sx={{ minWidth: 200 }} />
                      )}
                    />
                    {r.status === 'pending' && (
                      <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                        <Button size="small" sx={{ textTransform: 'none', fontSize: '0.7rem', minWidth: 0, p: '2px 6px' }} onClick={() => quickTag(r.id, 'Not Cannabis')}>
                          Not Cannabis
                        </Button>
                        <Button size="small" sx={{ textTransform: 'none', fontSize: '0.7rem', minWidth: 0, p: '2px 6px' }} onClick={() => quickTag(r.id, 'Dead')}>
                          Dead
                        </Button>
                      </Box>
                    )}
                  </TableCell>
                )}
                {statusFilter !== 'not_found' && (
                  <TableCell align="right">
                    {r.status === 'pending' && (
                      <>
                        <Tooltip title="Approve - write to company.website">
                          <span>
                            <IconButton size="small" color="success" disabled={busyIds.has(r.id) || !((edits[r.id] ?? r.candidate_url))} onClick={() => approve([r.id])}>
                              <CheckIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Reject">
                          <span>
                            <IconButton size="small" color="error" disabled={busyIds.has(r.id)} onClick={() => reject([r.id])}>
                              <RejectIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </>
                    )}
                    {r.status !== 'pending' && (
                      <Tooltip title="Clear and search again">
                        <span>
                          <IconButton size="small" disabled={busyIds.has(r.id)} onClick={() => retry([r.id])}>
                            <RetryIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default WebsiteFinderPage;
